import { HttpErrorResponse } from "@angular/common/http";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
} from "@angular/forms";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import { AlertController, ModalController } from "ionic-angular";
import moment from "moment";
import { BillingBenefitCode } from "../../../../models/payers/payer";
import { PayersService } from "../../../../providers/payers-service/payers.service";
import { UtilityService } from "../../../../providers/utility-service/utility-service";
import { NgbCustomParserFormatter } from "../../../common/date-picker/ngb-custom-parser-formatter";

@Component({
  selector: "billing-codes-rnb",
  templateUrl: "billing-codes-rnb.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BillingCodesRnb),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => BillingCodesRnb),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: NgbCustomParserFormatter },
  ],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class BillingCodesRnb implements OnInit, OnDestroy {
  @Input() currentPayer: any;
  @Input() currentPayerType: any;
  @Output() onCanceled = new EventEmitter<any>();
  @Output() setCheckMark = new EventEmitter<any>();
  @Output() validFields = new EventEmitter<boolean>();
  billingCodes: FormGroup;
  useMedicareBillingCodes: boolean = false;
  contractPeriodsOptions: any[] = [];
  selectedContractPeriod: any;
  billingCodesRNBData: any = [];
  benefitType: string = "ROOMANDBOARD";
  periodResponse: any = {};
  dateFormat: string = "YYYY-MM-DD";
  dateUIFormat: string = "MM/DD/YYYY";
  dateRegex = new RegExp("\\d{4}-\\d{2}-\\d{2}");
  dateFERegex = new RegExp("\\d{2}/\\d{2}/\\d{4}");
  minDateFormat: any = moment("2023-09-30", this.dateFormat);
  disableCodes: boolean = false;
  periodsResponse: any = [];
  private payerType: string;
  saveEnabled: boolean = false;
  codesEditDisabled: boolean = false;
  editMode: boolean = false;
  roomAndBoardBillingCodesSetup: BillingBenefitCode[] = [];

  setupBaseData() {
    this.roomAndBoardBillingCodesSetup = [
      {
        RevenueCode: "",
        HcpcsCode: "",
        Description: "Long-term Care or Non-Skilled Nursing Facility",
        Service: "NURSING_HOME",
        M1: "",
        M2: "",
        M3: "",
        M4: "",
      },
      {
        RevenueCode: "",
        HcpcsCode: "",
        Description: "Skilled Nursing Facility",
        Service: "SKILLED_NURSING_FACILITY",
        M1: "",
        M2: "",
        M3: "",
        M4: "",
      },
      {
        RevenueCode: "",
        HcpcsCode: "",
        Description: "Hospice Residential Facility",
        Service: "HOSPICE_RESIDENTIAL_FACILITY",
        M1: "",
        M2: "",
        M3: "",
        M4: "",
      },
      {
        RevenueCode: "",
        HcpcsCode: "",
        Description: "Place not otherwise specified",
        Service: "UNSPECIFIED",
        M1: "",
        M2: "",
        M3: "",
        M4: "",
      },
    ];
  }

  constructor(
    private utilityService: UtilityService,
    private payersService: PayersService,
    private alertController: AlertController,
    private modalCtrl: ModalController,
    private fb: FormBuilder
  ) {}

  ngOnDestroy(): void {}

  ngOnInit(): void {
    this.useMedicareBillingCodes = false;
    if (this.currentPayer && this.currentPayer.payerId) {
      this.loadBenefitPeriods(this.benefitType, null, true);
      this.payerType = this.currentPayer.payerType;
    }
    this.sortBenefitCodes();
    this.setupBaseData();
    this.initForm();
  }

  onPeriodChanged() {
    this.minDateFormat = moment("2023-09-30", this.dateFormat);
    this.loadBenefitPeriods(this.benefitType, null, true);
    this.selectedContractPeriod = null;
    this.billingCodesRNBData = [];
  }

  formatDate(date: any) {
    if (this.dateFERegex.test(date) || this.dateRegex.test(date))
      return moment(date).format(this.dateUIFormat);
    else return "--";
  }

  blankValue() {
    return "\xa0 -- ";
  }

  initForm() {
    this.billingCodes = this.fb.group({
      city: [],
      period: "10/01/2023",
    });
  }

  loadBenefitPeriods(payerType, subType, addNewOption) {
    let payerId = this.currentPayer.payerId;
    let payerTypeValue = payerType;
    let payerSubtype = subType;
    if (this.currentPayerType == "MEDICARE") {
      payerId = "MEDICARE";
    }
    this.payersService
      .getPayerBenefitCodesPeriods(payerId, payerTypeValue, payerSubtype)
      .subscribe((response: any) => {
        this.periodsResponse = response;
        this.contractPeriodsOptions = [];
        this.mapBenefitPeriods(response, addNewOption);
        this.sortBenefitCodesPeriods();
      });
  }

  mapBenefitPeriods(response: any, addNew: boolean) {
    if (addNew) this.addNewPeriodOption();
    response.map((period) => {
      let name = moment(period.start, this.dateFormat).format(
        this.dateUIFormat
      );
      let sDate = moment(period.start).format(this.dateFormat);
      let minDate = moment(this.minDateFormat).format(this.dateFormat);
      if (moment(sDate).isAfter(moment(minDate))) {
        this.minDateFormat = period.start;
      }
      let endDate = null;
      if (period.end && this.dateRegex.test(period.end)) endDate = period.end;
      if (endDate) {
        name += "-" + moment(period.end).format(this.dateUIFormat);
      }

      this.contractPeriodsOptions.push({
        id: this.contractPeriodsOptions.length + 1,
        name: name,
        startDate: period.start,
        endDate: endDate,
      });
    });
  }

  addNewPeriodOption(): void {
    this.contractPeriodsOptions = [
      { id: 0, name: "Add New" },
      ...this.contractPeriodsOptions,
    ];
  }

  async onContractPeriodChange(event: any) {
    if (!event) {
      this.billingCodesRNBData = [];
      return;
    }
    if (event.id === 0) {
      let contractPeriodModal = this.modalCtrl.create(
        "ContractPeriodModalComponent",
        { minDateFormat: this.minDateFormat },
        { cssClass: "contract-period-modal" }
      );
      contractPeriodModal.onDidDismiss((data) => {
        if (data) {
          let newContractPeriod = moment(new Date(data)).format(
            this.dateUIFormat
          );
          const newOption = {
            id: this.contractPeriodsOptions.length + 1,
            name: newContractPeriod,
            startDate: newContractPeriod,
            endDate: null,
          };
          this.billingCodesRNBData = [];
          this.useMedicareBillingCodes = true;
          this.medicareCheckBoxChanged();
          this.contractPeriodsOptions = [
            ...this.contractPeriodsOptions,
            newOption,
          ];
          this.selectedContractPeriod = newOption;
          this.contractPeriodsOptions.shift();
          this.setupBaseData();
          this.disableCodes = false;
          this.sortBenefitCodesPeriods();
          this.sortBenefitCodes();
          this.saveEnabled = true;
        } else {
          this.loadDefaults();
        }
      });

      contractPeriodModal.present();
    } else {
      this.selectedContractPeriod = event;
      this.utilityService.increaseSpinnerCount("Loading");
      this.loadBenefitPeriod(event);
      if (this.currentPayerType != "MEDICARE") {
        let filteredContractPeriods = this.contractPeriodsOptions.filter(
          (option) => option.startDate != this.selectedContractPeriod.startDate
        );
        if (filteredContractPeriods.length == 0) {
          this.useMedicareBillingCodes = true;
          this.medicareCheckBoxChanged();
        }
      }
    }
  }

  loadDefaults() {
    this.selectedContractPeriod = null;
    this.billingCodesRNBData = [];
    this.billingCodesRNBData = [...this.roomAndBoardBillingCodesSetup];
    this.sortBenefitCodes();
    this.useMedicareBillingCodes = false;
  }

  sortBenefitCodesPeriods() {
    this.contractPeriodsOptions = this.contractPeriodsOptions.sort((a, b) => {
      if (!a.startDate) return 0;
      if (!b.startDate) return 0;
      if (moment(a.startDate) > moment(b.startDate)) return -1;
      return 0;
    });
  }

  sortBenefitCodes() {
    const order = [
      "Long-term Care or Non-SKilled Nursing Facility",
      "Skilled Nursing Facility",
      "Hospice Residential Facility",
      "Place not otherwise specified",
    ];
    this.billingCodesRNBData = this.billingCodesRNBData.sort((a, b) => {
      const indexA = order.indexOf(a.Description);
      const indexB = order.indexOf(b.Description);
      return indexA - indexB;
    });
  }

  public loadBenefitPeriod(benefitPeriod) {
    let payerId = this.currentPayer.payerId;
    let benefitType = this.benefitType;
    let startDate = moment(benefitPeriod.startDate).format(this.dateFormat);
    let disableCodes = false;
    if (this.currentPayerType == "MEDICARE") {
      payerId = "MEDICARE";
      disableCodes = true;
    }
    this.payersService
      .getPayerBenefitCodesByPeriod(payerId, benefitType, null, startDate)
      .subscribe(
        (result: any) => {
          this.utilityService.decreaseSpinnerCount();
          this.periodResponse = result[0];
          if (this.periodResponse) {
            this.billingCodesRNBData = [];
            this.mapPeriodResponse();
            this.useMedicareBillingCodes = this.periodResponse.useDefaultCodes;
            this.disableCodes = disableCodes;
            this.saveEnabled = true;
          } else {
            this.billingCodesRNBData = [];
            this.billingCodesRNBData = [...this.roomAndBoardBillingCodesSetup];
          }
          this.sortBenefitCodes();
        },
        () => {
          this.billingCodesRNBData = [...this.roomAndBoardBillingCodesSetup];
          this.sortBenefitCodes();
        }
      );
  }

  mapPeriodResponse() {
    for (var key in this.periodResponse.codes) {
      if (!this.periodResponse.codes.hasOwnProperty(key)) continue;
      var obj = this.periodResponse.codes[key];
      let newPeriod: BillingBenefitCode = {
        RevenueCode: obj.revenueCode,
        HcpcsCode: obj.hcpcsCode,
        Description: obj.description,
        Service: obj.service,
        M1: obj.m1,
        M2: obj.m2,
        M3: obj.m3,
        M4: obj.m4,
      };
      this.billingCodesRNBData.push(newPeriod);
    }
    this.sortBenefitCodes();
  }

  showSavePayerModal() {
    if (
      this.currentPayer != null &&
      this.currentPayer.payerId &&
      this.selectedContractPeriod &&
      this.selectedContractPeriod.id > 0
    ) {
      let modalPage = this.modalCtrl.create(
        "SaveFacilityPage",
        {
          title: "Payer Information Change",
          commentPlaceholderKey: "archiveFacilityCommentPlaceHolder",
          popupMessageKey: "savePayerWarningMessage",
          section: "payer",
        },
        {
          enableBackdropDismiss: false,
          // NOVA-1908: payer confirm popup (section "payer") - use the payer
          // style hook so it is sized like the other payer tabs.
          cssClass: "save-payer-confirmation-popup",
        }
      );
      modalPage.onDidDismiss((data) => {
        if (data.save) {
          this.saveCodes();
        }
      });
      modalPage.present();
    } else {
      this.saveCodes();
    }
  }

  public saveCodes(): void {
    let request = this.createRequest();
    this.utilityService.increaseSpinnerCount("Loading");
    this.saveEnabled = false;
    this.payersService.postPayerBenefitCodes(request).subscribe(() => {
      setTimeout(() => {
        this.utilityService.decreaseSpinnerCount();
        let newOptionIndex = this.contractPeriodsOptions.findIndex(
          (option) => option.id === 0
        );
        this.loadBenefitPeriods(this.benefitType, null, newOptionIndex === -1);
        if (newOptionIndex === -1) this.addNewPeriodOption();
        this.saveEnabled = true;
        this.showAlert({
          title: "Success",
          message: "Billing Codes saved.",
        });
        this.setCheckMark.emit();
      }, 2000);
    }, this.onSaveError);
  }

  showAlert = ({ title, message }) => {
    let alert = this.alertController.create({
      title,
      message,
      buttons: [
        {
          text: "Ok",
        },
      ],
    });
    alert.present();
  };

  public validateFields() {
    return (
      this.currentPayer != null &&
      this.currentPayer.payerId &&
      this.selectedContractPeriod &&
      this.selectedContractPeriod.id > 0 &&
      this.validateCodeFields(this.billingCodesRNBData) &&
      this.saveEnabled
    );
  }

  public validateCodeFields(configuration) {
    let hcpcsCodeValue =
      configuration.filter(
        (x) => x.HcpcsCode.length > 0 && x.HcpcsCode.length < 5
      ).length > 0
        ? false
        : true;
    let revenueCodeValue =
      configuration.filter(
        (x) =>
          x.RevenueCode.length !== null &&
          x.RevenueCode.length !== "" &&
          x.RevenueCode.length == 4
      ).length == 4
        ? true
        : false;

    if (hcpcsCodeValue && revenueCodeValue) return true;
    else return false;
  }

  validatedFields(event: boolean) {
    // @ts-ignore
    this.validFields = event;
    this.saveEnabled = event;
  }

  private onSaveError = (error) => {
    this.saveEnabled = true;
    this.utilityService.decreaseSpinnerCount();
    if (error instanceof HttpErrorResponse) {
      if (error.error) {
        this.showAlert({
          title: "Error",
          message: error.error.message,
        });
      } else {
        this.showAlert({
          title: "Error",
          message: "Error while saving the rates. " + error.message,
        });
      }
    }
  };

  public createRequest(): any {
    let endDate = this.selectedContractPeriod
      ? moment(this.selectedContractPeriod.endDate).format(this.dateFormat)
      : null;
    if (!this.dateRegex.test(endDate)) endDate = null;
    let body = {
      payerId: this.currentPayer.payerId,
      dataType: "PayerServiceCodes",
      payerType: this.payerType,
      Start: moment(this.selectedContractPeriod.startDate).format(
        this.dateFormat
      ),
      End: endDate,
      BenefitType: this.benefitType,
      benefitSubType: null,
      UseDefaultCodes: this.useMedicareBillingCodes,
      Codes: {},
    };
    this.billingCodesRNBData.map((bc) => {
      body.Codes[bc.Service] = bc;
    });
    return body;
  }
  public cancel(): void {
    this.contractPeriodsOptions = [];
    this.onCanceled.emit(true);
  }

  async medicareCheckBoxChanged() {
    if (this.useMedicareBillingCodes) {
      this.setCheckBoxDataSetup();
      this.codesEditDisabled = false;
    } else {
      if (this.selectedContractPeriod) {
        let payerId = this.currentPayer.payerId;
        let benefitType = this.benefitType;
        let benefitSubtype = null;
        let startDate = moment(this.selectedContractPeriod.startDate).format(
          this.dateFormat
        );
        this.utilityService.increaseSpinnerCount("Loading");
        this.payersService
          .getPayerBenefitCodesByPeriod(
            payerId,
            benefitType,
            benefitSubtype,
            startDate
          )
          .subscribe(
            (result: any) => {
              this.utilityService.decreaseSpinnerCount();
              this.periodResponse = result[0];
              if (this.periodResponse) {
                this.mapPeriodResponseMedicareCheckbox();
              } else {
                this.setCheckBoxDataSetup();
              }
              this.codesEditDisabled = false;
              this.editMode = false;
              this.disableCodes = false;
            },
            () => {
              this.billingCodesRNBData = [];
              this.billingCodesRNBData = [
                ...this.roomAndBoardBillingCodesSetup,
              ];
            }
          );
      }
    }
  }

  async getMedicareContractPeriods() {
    return new Promise((resolve, reject) => {
      this.payersService
        .getPayerBenefitCodesPeriods("MEDICARE", this.benefitType, null)
        .subscribe(
          (response: any) => {
            if (response) {
              resolve(response);
            } else {
              resolve();
            }
          },
          (err) => {
            reject();
          }
        );
    });
  }

  async getActiveMedicareContractPeriod(payerId, startDate) {
    return new Promise((resolve, reject) => {
      this.payersService
        .getPayerBenefitCodesByPeriod(
          payerId,
          this.benefitType,
          null,
          startDate
        )
        .subscribe(
          (result: any) => {
            if (result) {
              resolve(result);
            } else {
              resolve();
            }
          },
          (err) => {
            reject();
          }
        );
    });
  }

  setCheckBoxDataSetup() {
    let nursingHomeOldValue = this.billingCodesRNBData.find(
      (value) =>
        value.Description.toLowerCase() ==
        "long-term care or non-skilled nursing facility"
    );
    let skilledNursingOldValue = this.billingCodesRNBData.find(
      (value) => value.Description == "Skilled Nursing Facility"
    );
    let hospiceResidentialOldValue = this.billingCodesRNBData.find(
      (value) => value.Description == "Hospice Residential Facility"
    );
    let unspecifiedOldValue = this.billingCodesRNBData.find(
      (value) => value.Description == "Place not otherwise specified"
    );
    this.billingCodesRNBData = [
      {
        RevenueCode: "0658",
        HcpcsCode: "Q5003",
        Description: "Long-term Care or Non-Skilled Nursing Facility",
        Service: "NURSING_HOME",
        M1: nursingHomeOldValue ? nursingHomeOldValue.M1 : "",
        M2: nursingHomeOldValue ? nursingHomeOldValue.M2 : "",
        M3: nursingHomeOldValue ? nursingHomeOldValue.M3 : "",
        M4: nursingHomeOldValue ? nursingHomeOldValue.M4 : "",
      },
      {
        RevenueCode: "0658",
        HcpcsCode: "Q5004",
        Description: "Skilled Nursing Facility",
        Service: "SKILLED_NURSING_FACILITY",
        M1: skilledNursingOldValue ? skilledNursingOldValue.M1 : "",
        M2: skilledNursingOldValue ? skilledNursingOldValue.M2 : "",
        M3: skilledNursingOldValue ? skilledNursingOldValue.M3 : "",
        M4: skilledNursingOldValue ? skilledNursingOldValue.M4 : "",
      },
      {
        RevenueCode: "0658",
        HcpcsCode: "Q5010",
        Description: "Hospice Residential Facility",
        Service: "HOSPICE_RESIDENTIAL_FACILITY",
        M1: hospiceResidentialOldValue ? hospiceResidentialOldValue.M1 : "",
        M2: hospiceResidentialOldValue ? hospiceResidentialOldValue.M2 : "",
        M3: hospiceResidentialOldValue ? hospiceResidentialOldValue.M3 : "",
        M4: hospiceResidentialOldValue ? hospiceResidentialOldValue.M4 : "",
      },
      {
        RevenueCode: "0658",
        HcpcsCode: "Q5009",
        Description: "Place not otherwise specified",
        Service: "UNSPECIFIED",
        M1: unspecifiedOldValue ? unspecifiedOldValue.M1 : "",
        M2: unspecifiedOldValue ? unspecifiedOldValue.M2 : "",
        M3: unspecifiedOldValue ? unspecifiedOldValue.M3 : "",
        M4: unspecifiedOldValue ? unspecifiedOldValue.M4 : "",
      },
    ];
  }

  mapPeriodResponseMedicareCheckbox() {
    let newData = [];
    for (var key in this.periodResponse.codes) {
      if (!this.periodResponse.codes.hasOwnProperty(key)) continue;
      var obj = this.periodResponse.codes[key];
      let oldValue = this.billingCodesRNBData.find(
        (value) => value.Description == obj.description
      );
      let newPeriod: BillingBenefitCode = {
        RevenueCode: obj.revenueCode,
        HcpcsCode: obj.hcpcsCode,
        Description: obj.description,
        Service: key,
        M1: oldValue ? oldValue.M1 : obj.m1,
        M2: oldValue ? oldValue.M2 : obj.m2,
        M3: oldValue ? oldValue.M3 : obj.m3,
        M4: oldValue ? oldValue.M4 : obj.m4,
      };

      newData.push(newPeriod);
    }

    this.billingCodesRNBData = [];
    this.billingCodesRNBData = newData;

    this.sortBenefitCodes();
  }
}



// WEBPACK FOOTER //
// ./src/components/payers/billing-codes/billing-codes-rnb/billing-codes-rnb.ts